import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  EmptyState,
  Icon,
  Illustration,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { PaymentLimitsTable } from 'components/PaymentLimits/PaymentLimitsTable'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetPaymentLimits } from 'hooks/queries/useGetPaymentLimits'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import { PaymentLimitSheet } from 'pages/PaymentLimits/components/PaymentLimitSheet'
import { LimitLevel, PaymentLimitSheetType } from 'types/paymentLimits'

interface CustomerPaymentLimitsTableProps {
  customerReference: string
}

export const CustomerPaymentLimitsTable = ({
  customerReference,
}: CustomerPaymentLimitsTableProps) => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()
  const permissions = useMappedRoles()
  const canEditPaymentLimits = permissions?.paymentLimits?.canEdit ?? false
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useSearchablePagination(MIN_PAGINATION_SIZE)

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)

  const { data: limitsData, isFetching } = useGetPaymentLimits({
    accountId: currentAccount?.reference,
    customerId: customerReference,
    pageNumber: page,
    pageSize: rowsPerPage,
    level: LimitLevel.CUSTOMER,
  })

  const limits = limitsData?.content ?? []
  const totalElements = limitsData?.totalElements ?? 0
  const hasLimits = limits?.length > 0

  if (isFetching) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (!hasLimits) {
    return (
      <>
        <EmptyState
          testId="customer-payment-limits-empty-state"
          LottiePlayer={<Illustration name="Welcome" />}
          title={t('customers.paymentLimitsTable.emptyStateTitle')}
          description={t('customers.paymentLimitsTable.emptyStateDescription')}
          {...(canEditPaymentLimits && {
            PrimaryButton: (
              <Button
                testId="add-customer-limit-button"
                variant="secondary"
                iconElement={<Icon name="Plus" />}
                onClick={() => setIsAddSheetOpen(true)}
              >
                {t('paymentLimits.addLimit')}
              </Button>
            ),
          })}
        />

        <PaymentLimitSheet
          isOpen={isAddSheetOpen}
          type={PaymentLimitSheetType.ADD}
          customerId={customerReference}
          onClose={() => setIsAddSheetOpen(false)}
        />
      </>
    )
  }

  return (
    <>
      {canEditPaymentLimits && (
        <Box flex justify="end" paddingB={16}>
          <Button
            testId="add-customer-limit-button"
            variant="secondary"
            iconElement={<Icon name="Plus" />}
            onClick={() => setIsAddSheetOpen(true)}
          >
            {t('paymentLimits.addLimit')}
          </Button>
        </Box>
      )}

      <PaymentLimitsTable
        limits={limits}
        tableName="CustomerPaymentLimitsTable"
        customerId={customerReference}
        page={page}
        rowsPerPage={rowsPerPage}
        totalElements={totalElements}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        customerId={customerReference}
        onClose={() => setIsAddSheetOpen(false)}
      />
    </>
  )
}
